import React, { useState } from 'react';
import { Tabs, Image, Badge } from 'antd';
import { motion, AnimatePresence } from 'framer-motion';
import { track } from '../analytics';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Zap, BarChart3, Brain } from 'lucide-react';

const ProductTour = () => {
  const [activeTab, setActiveTab] = useState('task');
  const handleTabChange = (key) => {
    setActiveTab(key);
    try { track('product_tour_tab_change', { tab: key }); } catch {}
  };
  const { t } = useTranslation();

  const tabs = [
    {
      key: 'task',
      label: t('productTour.tabs.task'),
      image: '/assets/product-tour/Agricultural Task Management in Action.png',
      icon: <CheckCircle size={24} />,
      bullets: t('productTour.bullets.task', { returnObjects: true }),
      color: 'from-blue-500 to-cyan-500',
      badge: t('productTour.badges.essential')
    },
    {
      key: 'crop',
      label: t('productTour.tabs.crop'),
      image: '/assets/product-tour/Satellite Monitoring Agricultural Health.png',
      icon: <Zap size={24} />,
      bullets: t('productTour.bullets.crop', { returnObjects: true }),
      color: 'from-green-500 to-emerald-500',
      badge: t('productTour.badges.core')
    },
    {
      key: 'analytics',
      label: t('productTour.tabs.analytics'),
      image: '/assets/product-tour/Precision Agriculture Data Analysis.png',
      icon: <BarChart3 size={24} />,
      bullets: t('productTour.bullets.analytics', { returnObjects: true }),
      color: 'from-purple-500 to-pink-500',
      badge: t('productTour.badges.premium')
    },
    {
      key: 'intelligence',
      label: t('productTour.tabs.intelligence'),
      image: '/assets/product-tour/AI Crop Forecasting Map.png',
      icon: <Brain size={24} />,
      bullets: t('productTour.bullets.intelligence', { returnObjects: true }),
      color: 'from-orange-500 to-red-500',
      badge: t('productTour.badges.ai')
    }
  ];

  const activeTabData = tabs.find(tab => tab.key === activeTab);

  return (
    <div className="py-32 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-20 right-20 w-64 h-64 bg-gradient-to-br from-primary/10 to-green-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-20 w-80 h-80 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <h2 className="text-5xl md:text-6xl font-black mb-6 bg-gradient-to-r from-dark via-primary to-dark bg-clip-text text-transparent">{t('productTour.title')}</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">{t('productTour.subtitle')}</p>
        </motion.div>

        <div className="max-w-7xl mx-auto">
          <Tabs
            activeKey={activeTab}
            onChange={handleTabChange}
            centered
            size="large"
            className="custom-tabs"
            items={tabs.map(tab => ({
              key: tab.key,
              label: (
                <div className="flex items-center space-x-3 px-4 py-2">
                  <div className={`p-2 rounded-lg bg-gradient-to-r ${tab.color} text-white`}>
                    {tab.icon}
                  </div>
                  <div className="text-left">
                    <div className="font-semibold">{tab.label}</div>
                    <Badge 
                      count={tab.badge} 
                      style={{ 
                        backgroundColor: tab.key === activeTab ? '#2E7D32' : '#gray-400',
                        fontSize: '10px',
                        height: '18px',
                        lineHeight: '18px'
                      }} 
                    />
                  </div>
                </div>
              ),
              children: null
            }))}
          />

          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="mt-12"
            >
              <div className="grid lg:grid-cols-2 gap-16 items-center">
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <div className="relative group">
                    <div className={`absolute -inset-4 bg-gradient-to-r ${activeTabData.color} rounded-3xl blur-xl opacity-20 group-hover:opacity-30 transition-opacity duration-300`}></div>
                    <div className="relative bg-white rounded-3xl p-4 shadow-2xl">
                      <Image.PreviewGroup>
                        <Image
                          src={activeTabData.image}
                          alt={activeTabData.label}
                          className="rounded-2xl w-full"
                          loading="lazy"
                          style={{ borderRadius: '16px' }}
                        />
                      </Image.PreviewGroup>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="space-y-8"
                >
                  <div>
                    <div className="flex items-center space-x-4 mb-6">
                      <div className={`p-4 rounded-2xl bg-gradient-to-r ${activeTabData.color} text-white shadow-lg`}>
                        {activeTabData.icon}
                      </div>
                      <div>
                        <h3 className="text-3xl font-bold text-dark">{activeTabData.label}</h3>
                        <Badge 
                          count={activeTabData.badge} 
                          style={{ 
                            backgroundColor: '#2E7D32',
                            fontSize: '12px'
                          }} 
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {activeTabData.bullets.map((bullet, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: 0.6 + index * 0.1 }}
                        className="flex items-center space-x-4 p-4 bg-white rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300"
                      >
                        <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${activeTabData.color} flex-shrink-0`}></div>
                        <span className="text-lg text-gray-700 font-medium">{bullet}</span>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default ProductTour;
